
import React, { useState, useRef } from 'react';
import { Upload, Trash2, X, RotateCcw } from 'lucide-react';
import { LogoLayoutSelector } from './LogoLayoutSelector';
import { LogoLayout } from './useLogoUpload';
import { Button } from '@/components/ui/button';

interface LogoUploadSectionProps {
  logoPreview: string | null;
  uploadError: string | null;
  logoLayout: LogoLayout;
  setLogoLayout: (layout: LogoLayout) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveLogo: () => void;
  handleReset?: () => void;
}

export const LogoUploadSection: React.FC<LogoUploadSectionProps> = ({
  logoPreview,
  uploadError,
  logoLayout,
  setLogoLayout,
  handleFileChange,
  handleRemoveLogo,
  handleReset
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Handle click on the upload area
  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };
  
  // Handle drag events
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a synthetic event to pass to the handleFileChange
      const syntheticEvent = {
        target: {
          files
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      handleFileChange(syntheticEvent);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="text-lg font-medium">Upload Logo</div>
      
      {/* Logo preview area - made clickable and droppable */}
      <div 
        className={`border rounded-md h-[200px] flex items-center justify-center ${isDragging ? 'bg-gray-100 border-primary border-dashed' : 'bg-gray-50'} relative overflow-hidden cursor-pointer`}
        onClick={!logoPreview ? handleUploadAreaClick : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {logoPreview ? (
          <>
            <img 
              src={logoPreview} 
              alt="Logo preview" 
              className="max-w-full max-h-full object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <Button 
                variant="destructive" 
                size="icon" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveLogo();
                }}
                title="Remove logo"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center px-4">
            <Upload size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">
              Drag and drop your logo here, or click to upload
            </p>
          </div>
        )}
      </div>
      
      {/* Error message */}
      {uploadError && (
        <div className="text-sm text-red-500">{uploadError}</div>
      )}
      
      {/* Upload and Reset buttons in a 50/50 row */}
      <div className="grid grid-cols-2 gap-2">
        <label htmlFor="logo-upload" className="cursor-pointer col-span-1">
          <div className="inline-flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Upload size={16} className="mr-2" />
            {logoPreview ? 'Change Logo' : 'Upload Logo'}
          </div>
          <input
            ref={fileInputRef}
            id="logo-upload"
            type="file"
            className="sr-only"
            onChange={handleFileChange}
            accept="image/*"
          />
        </label>

        {handleReset && (
          <Button 
            variant="outline" 
            onClick={handleReset} 
            className="col-span-1"
            disabled={!logoPreview}
          >
            <RotateCcw size={16} className="mr-2" />
            Reset
          </Button>
        )}
      </div>
      
      {/* Logo layout selector - only show when a logo is uploaded */}
      {logoPreview && (
        <LogoLayoutSelector 
          value={logoLayout}
          onChange={setLogoLayout}
        />
      )}
      
      <div className="text-sm text-gray-600 mt-2">
        Upload a clean, high-resolution image of your logo. We recommend using a PNG or SVG file with a transparent background for best results.
      </div>
    </div>
  );
};
